import {
    attachmentService,
    AuthContext,
    githubStorageService
} from "@harvestr-api/common";
import {
    gqlAttachment,
    gqlDiscovery,
    gqlGithubIssue,
    gqlGithubIssueCreateWithoutDiscoveryInput
} from "@harvestr-api/models";
import {
    htmlToMarkdown,
    httpRequestPaginated,
    logger,
    parallelPromiseHelper,
    toClientId
} from "@harvestr-api/utils";
import * as request from "request-promise";
import { getGithubBearer, getGithubToken } from "../helpers/github-auth.helper";
import {
    GithubComment,
    GithubIssue,
    GithubLabel,
    GithubMilestone,
    GithubOrganization,
    GithubRepository,
    GithubUser
} from "../models/github";

export const githubApiService = {
    async urlGet(data: { url: string }, ctx: AuthContext) {
        return request.get({
            url: data.url,
            auth: await getGithubBearer(ctx),
            qs: {},
            json: true,
            headers: { "user-agent": "node.js" }
        });
    },
    async createIssue(
        data: {
            harvestrIssue: gqlGithubIssueCreateWithoutDiscoveryInput;
            discovery: Pick<gqlDiscovery, "id" | "title" | "clientId">;
            copyDiscoveryAttachments: boolean;
        },
        ctx: AuthContext
    ): Promise<gqlGithubIssueCreateWithoutDiscoveryInput> {
        const isOwnerName =
            data.harvestrIssue.githubOwnerName !== null &&
            data.harvestrIssue.githubOwnerName !== "";
        const user = isOwnerName ? {} : await githubApiService.getUser(ctx);

        const validatedHarvestrIssue = githubApiService.validateGithubIssue(
            data.harvestrIssue
        );

        const githubIssueCreated = await request.post({
            url: `https://api.github.com/repos/${
                isOwnerName
                    ? validatedHarvestrIssue.githubOwnerName
                    : user.login
            }/${validatedHarvestrIssue.githubRepositoryName}/issues`,
            auth: await getGithubBearer(ctx),
            body: githubApiService.toGithubIssue(data.harvestrIssue),
            json: true,
            headers: { "user-agent": "node.js" }
        });
        logger.info(
            "githuApiService.createIssue() : before githubApiService.linkIssue"
        );
        const fullHarvestrIssue = await githubApiService.toHarvestrGithubIssue(
            githubIssueCreated,
            validatedHarvestrIssue,
            ctx
        );
        await githubApiService.linkIssue(
            {
                harvestrIssue: fullHarvestrIssue,
                discovery: data.discovery,
                copyDiscoveryAttachments: data.copyDiscoveryAttachments
            },
            ctx
        );
        logger.info(
            "githuApiService.createIssue() : after githubApiService.linkIssue"
        );
        if (
            typeof data.harvestrIssue.githubColumnId === "number" &&
            data.harvestrIssue.githubColumnId > -1
        ) {
            await githubApiService.createCard(
                {
                    column_id: data.harvestrIssue.githubColumnId,
                    issue_id: githubIssueCreated.id
                },
                ctx
            );
        }
        return fullHarvestrIssue;
    },
    async createCard(
        data: {
            column_id: number;
            issue_id: number;
        },
        ctx
    ): Promise<GithubIssue> {
        return request.post({
            url: `https://api.github.com/projects/columns/${data.column_id}/cards`,
            auth: await getGithubBearer(ctx),
            body: {
                content_id: data.issue_id,
                content_type: "Issue"
            },
            json: true,
            headers: {
                "user-agent": "node.js",
                Accept: "application/vnd.github.inertia-preview+json"
            }
        });
    },

    async linkIssue(
        data: {
            harvestrIssue: gqlGithubIssueCreateWithoutDiscoveryInput;
            discovery: Pick<gqlDiscovery, "id" | "title">;
            copyDiscoveryAttachments: boolean;
        },
        ctx: AuthContext
    ) {
        const fullGithubIssue = await githubApiService.getIssue(
            {
                ownerName: data?.harvestrIssue.githubOwnerName || "",
                repositoryName: data?.harvestrIssue?.githubRepositoryName || "",
                issueNumber: data?.harvestrIssue?.githubNumber || 0
            },
            ctx
        );

        const listAttachmentsOfIssue = await githubApiService.getCommentsOfIssue(
            {
                ownerName: data?.harvestrIssue.githubOwnerName || "",
                repositoryName: data?.harvestrIssue?.githubRepositoryName || "",
                issueNumber: data?.harvestrIssue?.githubNumber || 0
            },
            ctx
        );

        const setupDiscoveryAttachments = async () => {
            if (data.copyDiscoveryAttachments === false) {
                return [];
            }
            const discoveryAttachments: Array<gqlAttachment> = await attachmentService.getAttachmentsOfDiscovery(
                { discoveryId: data.discovery.id }
            );
            return discoveryAttachments.map(attachment => ({
                url: attachment.url,
                body: `**Harvestr discovery file-attachment:** [${
                    attachment?.filename || ""
                }](${attachment?.url})`
            }));
        };

        // ? create a link between github issue and harvestr discovery's attachments
        const linkToDiscovery = {
            url: `${process.env.H_CLIENT_URL}/components/0/list/${
                toClientId(data?.discovery)?.id
            }`,
            body: `**Issue was linked to Harvestr Discovery:**  [${
                data?.discovery?.title || ""
            }](${process.env.H_CLIENT_URL}/components/0/list/${
                toClientId(data?.discovery)?.id
            })`
        };

        const candidateAttachments = [
            linkToDiscovery,
            ...(await setupDiscoveryAttachments())
        ].filter(candidateAttachment => {
            const attachmentAlreadySet = listAttachmentsOfIssue.some(
                attachmentOfIssue => {
                    return attachmentOfIssue.body.includes(
                        candidateAttachment.url
                    );
                }
            );
            return attachmentAlreadySet === false;
        });

        await parallelPromiseHelper.uploadData(
            candidateAttachments || [],
            async attachmentsChunk => {
                return (attachmentsChunk || []).map(attachment => {
                    return githubApiService.createComment(
                        {
                            ownerName:
                                data?.harvestrIssue.githubOwnerName || "",
                            repositoryName:
                                data?.harvestrIssue?.githubRepositoryName || "",
                            issueNumber: data?.harvestrIssue?.githubNumber || 0,
                            comment: {
                                body: attachment.body
                            }
                        },
                        ctx
                    );
                });
            },
            5
        );

        const convertedIssue = await githubApiService.toHarvestrGithubIssue(
            fullGithubIssue,
            data.harvestrIssue,
            ctx
        );
        return convertedIssue;
    },

    async hasIntegrationEnabled(projectId: string): Promise<boolean> {
        const integrationToken = await getGithubToken({
            project_id: projectId,
            type: "INTEGRATION_GITHUB"
        });
        return integrationToken !== undefined;
    },

    async syncIssues(
        where: { projectsIds: string[] },
        apis: {
            devtools: {
                canSyncToday: (
                    devtoolName: string,
                    redisKey: string
                ) => Promise<boolean>;
                BASE_REDIS_ISSUES_SYNC_KEY: Readonly<string>;
            };
        }
    ) {
        console.log("github.syncIssues() --------------------");

        console.log(
            "github.syncIssues() : where.projectsIds : ",
            where.projectsIds
        );

        const tasks = where.projectsIds.map(async projectId => {
            const integrationStateEnabled = await githubApiService.hasIntegrationEnabled(
                projectId
            );
            if (integrationStateEnabled === false) {
                return [];
            } else {
                const REDIS_KEY_LAST_SYNC = `${apis.devtools.BASE_REDIS_ISSUES_SYNC_KEY}${projectId}`;
                const canSyncNow = await apis.devtools.canSyncToday(
                    "github",
                    REDIS_KEY_LAST_SYNC
                );
                if (canSyncNow === false) {
                    console.log(
                        `github : sync OFF for projectId ${projectId} : last sync less than 24h`
                    );
                    return [];
                } else {
                    console.log(`github : sync ON for projectId ${projectId}`);
                    const issues = (
                        await githubStorageService.getIssuesOfProjectForSync({
                            projectId
                        })
                    ).filter(
                        issue =>
                            String(issue.githubId) !== "0" &&
                            issue.githubId !== null
                    );

                    return githubApiService.bulkSyncIssuesOfProjects(issues, {
                        project_id: projectId,
                        type: "INTEGRATION_GITHUB"
                    });
                }
            }
        });

        console.log("github.syncIssues(): END");
        return tasks;
    },

    async bulkSyncIssuesOfProjects(
        hvrIssues: gqlGithubIssue[],
        ctx: AuthContext
    ) {
        return parallelPromiseHelper.uploadData(
            hvrIssues,
            async hvrIssuesChunk => {
                return hvrIssuesChunk.map(async hvrIssue => {
                    try {
                        const issueFromGithub: any = await githubApiService.getIssue(
                            {
                                ownerName: hvrIssue?.githubOwnerName || "",
                                repositoryName:
                                    hvrIssue?.githubRepositoryName || "",
                                issueNumber: hvrIssue?.githubNumber || -1
                            },
                            ctx
                        );
                        const convertedIssue = await githubApiService.toHarvestrGithubIssue(
                            issueFromGithub,
                            undefined
                        );
                        if (
                            issueFromGithub &&
                            issueFromGithub?.id &&
                            issueFromGithub?.githubNumber &&
                            issueFromGithub?.githubNumber > 0 &&
                            typeof issueFromGithub === "object"
                        ) {
                            return githubStorageService.updateIssue(
                                issueFromGithub.id,
                                convertedIssue
                            );
                        } else {
                            return Promise.resolve();
                        }
                    } catch (error) {
                        if (
                            error.statusCode === 401 ||
                            error.statusCode === 404 ||
                            error.statusCode === 429
                        ) {
                            console.log(
                                "issue not found or over-rate limit : githubNumber : ",
                                hvrIssue.githubNumber
                            );
                            return false;
                        }
                    }
                });
            }
        );
    },

    async createComment(
        data: {
            ownerName: string | null;
            repositoryName: string;
            issueNumber: number;
            comment: {
                body: string;
            };
        },
        ctx: AuthContext
    ): Promise<GithubComment> {
        const user =
            data.ownerName && data.ownerName !== null
                ? {}
                : await githubApiService.getUser(ctx);

        return request.post({
            url: `https://api.github.com/repos/${
                data.ownerName && data.ownerName !== null
                    ? data.ownerName
                    : user.login
            }/${data.repositoryName}/issues/${data.issueNumber}/comments`,
            auth: await getGithubBearer(ctx),
            body: data.comment,
            json: true,
            headers: { "user-agent": "node.js" }
        });
    },
    async getCommentsOfIssue(
        data: {
            ownerName: string | null;
            repositoryName: string;
            issueNumber: number;
        },
        ctx: AuthContext
    ): Promise<Array<GithubComment>> {
        const user =
            data.ownerName && data.ownerName !== null
                ? {}
                : await githubApiService.getUser(ctx);

        return request.get({
            url: `https://api.github.com/repos/${
                data.ownerName && data.ownerName !== null
                    ? data.ownerName
                    : user.login
            }/${data.repositoryName}/issues/${data.issueNumber}/comments`,
            auth: await getGithubBearer(ctx),
            json: true,
            headers: { "user-agent": "node.js" }
        });
    },

    getUser: async function (ctx: AuthContext, gotTokenAlready?: string) {
        return request.get({
            url: `https://api.github.com/user`,
            auth: await getGithubBearer(ctx, gotTokenAlready),
            qs: {},
            json: true,
            headers: { "user-agent": "node.js" }
        });
    },

    getUserOrgs: async function (ctx: AuthContext, gotTokenAlready?: string) {
        return request.get({
            url: `https://api.github.com/user/orgs`,
            auth: await getGithubBearer(ctx),
            qs: {},
            json: true,
            headers: { "user-agent": "node.js" }
        });
    },

    getUserRepositories: async function (
        ctx: AuthContext,
        gotTokenAlready?: string
    ) {
        return request.get({
            url: `https://api.github.com/user/repos`,
            auth: await getGithubBearer(ctx, gotTokenAlready),
            qs: {},
            json: true,
            headers: { "user-agent": "node.js" }
        });
    },

    async getUserPermissionsOnRepo(
        where: {
            repoName: string;
            username: string;
        },
        ctx: AuthContext,
        gotTokenAlready?: string,
        caller?: string
    ) {
        return request.get({
            url: `https://api.github.com/repos/${where.username}/${where.repoName}/collaborators/${where.username}/permission`,
            auth: await getGithubBearer(ctx, gotTokenAlready),
            qs: {},
            json: true,
            headers: { "user-agent": "node.js" }
        });
    },

    async hasUserRequiredPermissions(
        where: { user?: any },
        ctx: AuthContext,
        gotTokenAlready?: string
    ) {
        const GITHUB_USER_PERMISSIONS_ALLOWED = [
            "admin",
            "maintainer",
            "member"
        ];
        const user =
            where?.user ||
            (await githubApiService.getUser(ctx, gotTokenAlready));
        const userRepos = await githubApiService.getUserRepositories(
            ctx,
            gotTokenAlready
        );

        const hasRequiredPermission = (userRepos || []).reduce(
            async (hasRightAccessLevel, repo) => {
                try {
                    const userPermssionTypeOnRepo = await githubApiService.getUserPermissionsOnRepo(
                        {
                            repoName: repo.name,
                            username: user.login
                        },
                        ctx,
                        gotTokenAlready
                    );
                    hasRightAccessLevel =
                        hasRightAccessLevel &&
                        GITHUB_USER_PERMISSIONS_ALLOWED.includes(
                            userPermssionTypeOnRepo.permission
                        );
                    return hasRightAccessLevel;
                } catch (err) {
                    return hasRightAccessLevel;
                }
            },
            true
        );
        return hasRequiredPermission;
    },

    async getOrganizations(
        ctx: AuthContext,
        gotTokenAlready?: string
    ): Promise<GithubOrganization[]> {
        const user = await githubApiService.getUser(ctx, gotTokenAlready);
        return request.get({
            url: user.organizations_url,
            auth: await getGithubBearer(ctx, gotTokenAlready),
            qs: {},
            json: true,
            headers: { "user-agent": "node.js" }
        });
    },

    async getOrganizationUsers(
        data: {
            organization_name: string | null;
        },
        ctx: AuthContext
    ): Promise<GithubUser[]> {
        if (
            data.organization_name === null ||
            data.organization_name === "null"
        ) {
            return [await githubApiService.getUser(ctx)];
        }

        const httpRequest = async (
            pageNumber,
            itemsPerPage
        ): Promise<GithubUser[]> => {
            return request.get({
                url: `https://api.github.com/orgs/${data.organization_name}/members?page=${pageNumber}&per_page=${itemsPerPage}`,
                auth: await getGithubBearer(ctx),
                qs: {},
                json: true,
                headers: { "user-agent": "node.js" }
            });
        };

        return httpRequestPaginated<GithubUser>({
            httpRequest,
            pageStartsAt: 1,
            itemsPerPage: 100
        });
    },

    async getRepositories(
        data: { organization_name?: string; user_name?: string },
        ctx: AuthContext,
        gotTokenAlready?: string
    ): Promise<GithubRepository[]> {
        const httpRequest = async (
            currentPageNumber: number,
            itemsPerPage: number | undefined
        ): Promise<GithubRepository[]> => {
            let url = "";
            if (data.organization_name !== undefined) {
                url = `https://api.github.com/orgs/${data.organization_name}/repos?page=${currentPageNumber}&per_page=100`;
            } else {
                const user =
                    data.user_name ||
                    (await githubApiService.getUser(ctx, gotTokenAlready))
                        .login;
                url = `https://api.github.com/users/${user}/repos?page=${currentPageNumber}&per_page=${
                    itemsPerPage || 100
                }`;
            }

            return request.get({
                url,
                auth: await getGithubBearer(ctx, gotTokenAlready),
                qs: {},
                json: true,
                headers: { "user-agent": "node.js" }
            });
        };
        return httpRequestPaginated<GithubRepository>({
            httpRequest,
            pageStartsAt: 1,
            itemsPerPage: 100
        });
    },

    async getRepository(
        data: {
            owner_name: string;
            repository_name: string;
        },
        ctx: AuthContext
    ): Promise<GithubRepository> {
        const owner = await getOwner(data.owner_name, ctx);
        return request.get({
            url: `https://api.github.com/repos/${owner}/${data.repository_name}`,
            auth: await getGithubBearer(ctx),
            qs: {},
            json: true,
            headers: { "user-agent": "node.js" }
        });
    },
    async searchIssues(
        data: {
            owner_name: string | undefined;
            repository_name: string;
            query: string;
        },
        ctx: AuthContext
    ): Promise<GithubIssue[]> {
        const httpRequest = async (
            currentPageNumber: number,
            itemsPerPage: number
        ): Promise<GithubIssue[]> => {
            const owner = await getOwner(data.owner_name, ctx);
            return (
                await request.get({
                    url: `https://api.github.com/search/issues?page=${currentPageNumber}&per_page=${itemsPerPage}&q=repo:${owner}/${
                        data.repository_name
                    }+state:open+${data.query || ""}`,
                    auth: await getGithubBearer(ctx),
                    qs: {},
                    json: true,
                    headers: { "user-agent": "node.js" }
                })
            ).items;
        };
        return httpRequestPaginated<GithubIssue>({
            httpRequest,
            pageStartsAt: 1,
            itemsPerPage: 100
        });
    },
    async getIssue(
        data: {
            ownerName: string;
            repositoryName: string;
            issueNumber: number;
        },
        ctx: AuthContext
    ): Promise<GithubIssue> {
        const owner = await getOwner(data.ownerName, ctx);
        return request.get({
            url: `https://api.github.com/repos/${owner}/${data.repositoryName}/issues/${data.issueNumber}`,
            auth: await getGithubBearer(ctx),
            qs: {},
            json: true,
            headers: { "user-agent": "node.js" }
        });
    },

    async getLabels(
        data: {
            owner_name: string;
            repository_name: string;
        },
        ctx: AuthContext
    ): Promise<GithubLabel[]> {
        const owner = await getOwner(data.owner_name, ctx);
        return request.get({
            url: `https://api.github.com/repos/${owner}/${data.repository_name}/labels`,
            auth: await getGithubBearer(ctx),
            qs: {},
            json: true,
            headers: { "user-agent": "node.js" }
        });
    },

    async getMilestones(
        data: {
            owner_name: string;
            repository_name: string;
        },
        ctx: AuthContext
    ): Promise<GithubMilestone[]> {
        const owner = await getOwner(data.owner_name, ctx);
        return request.get({
            url: `https://api.github.com/repos/${owner}/${data.repository_name}/milestones`,
            auth: await getGithubBearer(ctx),
            qs: {
                state: "open"
            },
            json: true,
            headers: { "user-agent": "node.js" }
        });
    },
    async getRepositoryProjects(
        data: {
            owner_name: string;
            repository_name: string;
        },
        ctx: AuthContext
    ): Promise<any> {
        const httpRequest = async (
            pageNumber,
            itemsPerPage
        ): Promise<any[]> => {
            const owner = await getOwner(data.owner_name, ctx);
            return request.get({
                url: `https://api.github.com/repos/${owner}/${data.repository_name}/projects?page=${pageNumber}&per_page=${itemsPerPage}`,
                auth: await getGithubBearer(ctx),
                qs: { state: "open" },
                json: true,
                headers: {
                    "user-agent": "node.js",
                    Accept: "application/vnd.github.inertia-preview+json"
                }
            });
        };

        return httpRequestPaginated<GithubIssue>({
            httpRequest,
            pageStartsAt: 1,
            itemsPerPage: 100
        });
    },
    async getProjectColumns(
        data: {
            project_id: number;
        },
        ctx: AuthContext
    ): Promise<any> {
        return request.get({
            url: `https://api.github.com/projects/${data.project_id}/columns`,
            auth: await getGithubBearer(ctx),
            qs: {},
            json: true,
            headers: {
                "user-agent": "node.js",
                Accept: "application/vnd.github.inertia-preview+json"
            }
        });
    },

    async getProjectColumnCards(
        data: {
            project_id: number;
            column_id: number;
        },
        ctx: AuthContext
    ): Promise<any> {
        return request.get({
            url: `https://api.github.com/projects/${data.project_id}/columns/${data.column_id}`,
            auth: await getGithubBearer(ctx),
            qs: {},
            json: true,
            headers: {
                "user-agent": "node.js",
                Accept: "application/vnd.github.inertia-preview+json"
            }
        });
    },

    async getColumnPosition(
        data: {
            project_id: number | null;
            column_id: number | null;
        },
        ctx: AuthContext
    ): Promise<number> {
        if (data.project_id === null || data.column_id === null) {
            return -1;
        }
        console.log("continue fetch columns");
        const columns = await githubApiService.getProjectColumns(
            { project_id: data.project_id },
            ctx
        );
        return columns.findIndex(cl => cl.id === data.column_id);
    },

    async toHarvestrGithubIssue(
        githubIssue: any,
        originIssue?: gqlGithubIssueCreateWithoutDiscoveryInput,
        ctx?: AuthContext
    ): Promise<gqlGithubIssueCreateWithoutDiscoveryInput> {
        const getRepositoryName = repositoryUrl =>
            repositoryUrl.split("/").pop();

        const getOwnerName = repositoryUrl => {
            const split = repositoryUrl.split("/");
            return split.slice(split.length - 2, split.length - 1).shift();
        };

        return {
            id: originIssue?.id,
            githubId: parseInt(githubIssue?.id),
            githubNumber: githubIssue?.number,
            githubUrl: githubIssue?.html_url,
            githubDescription: githubIssue?.body || "",
            githubRepositoryName: getRepositoryName(
                githubIssue?.repository_url
            ),
            githubRepositoryUrl: githubIssue?.repository_url,
            githubOwnerName: getOwnerName(githubIssue?.repository_url),
            githubProjectId: originIssue?.githubProjectId || -1,
            githubTitle: githubIssue?.title,
            githubUserId: githubIssue?.user?.id || -1,
            githubUserLogin: githubIssue?.user?.login || "",
            githubLabels: (githubIssue?.labels || [])
                .filter(l => l !== null)
                .map(({ name }) => name),
            githubStateName: githubIssue?.state || "",
            githubColumnId: originIssue?.githubColumnId || 0,
            githubColumnName: originIssue?.githubColumnName || "",
            githubColumnPosition:
                (originIssue?.githubProjectId || -1) !== -1 &&
                (originIssue?.githubColumnId || -1) !== -1
                    ? await githubApiService.getColumnPosition(
                          {
                              project_id: originIssue?.githubProjectId || null,
                              column_id: originIssue?.githubColumnId || null
                          },
                          ctx as AuthContext
                      )
                    : -1,
            githubLocked: githubIssue?.locked || false,
            githubAssigneeId: githubIssue?.assignee?.id || -1,
            githubAssigneeLogin: githubIssue?.assignee?.login || "",
            githubAssigneeAvatarUrl: githubIssue?.assignee?.avatar_url || "",
            githubMilestoneId: githubIssue?.milestone
                ? githubIssue?.milestone?.id
                : -1,
            githubMilestoneNumber: githubIssue?.milestone?.number || -1,
            githubMilestoneTitle: githubIssue?.milestone?.title || "",
            githubCommentsCount: githubIssue?.comments || 0
        };
    },

    validateGithubIssue(
        githubIssue: gqlGithubIssueCreateWithoutDiscoveryInput
    ): gqlGithubIssueCreateWithoutDiscoveryInput {
        const markdownService = htmlToMarkdown({
            rules: {
                weblink: {
                    filter: ["a"],
                    replacement: (content, domNode) =>
                        `[${content}](${domNode.getAttribute("href")})`
                },
                strikethrough: {
                    filter: ["s"],
                    replacement: content => `~~${content}~~`
                },
                bold: {
                    filter: ["strong", "b"],
                    replacement: content => `**${content}**`
                },
                heading1: {
                    filter: ["h1"],
                    replacement: content => `# ${content}`
                },
                heading2: {
                    filter: ["h2"],
                    replacement: content => `## ${content}`
                }
            }
        });
        return {
            ...githubIssue,
            githubDescription: markdownService.htmlToMarkdown(
                githubIssue?.githubDescription || ""
            )
        };
    },

    toGithubIssue(
        harvestrGithubIssue: gqlGithubIssueCreateWithoutDiscoveryInput
    ) {
        return {
            owner_name: harvestrGithubIssue?.githubOwnerName || "",
            repository_name: harvestrGithubIssue?.githubRepositoryName,
            column_id: harvestrGithubIssue?.githubColumnId,
            assignee: harvestrGithubIssue?.githubAssigneeLogin,
            title: harvestrGithubIssue?.githubTitle,
            body: harvestrGithubIssue?.githubDescription,
            milestone: harvestrGithubIssue?.githubMilestoneNumber,
            labels: [...(harvestrGithubIssue?.githubLabels || [])]
        };
    }
};

async function getOwner(ownerName, ctx) {
    const isOrgDefined = ![undefined, "undefined", null, ""].includes(
        ownerName
    );
    return isOrgDefined
        ? ownerName
        : (await githubApiService.getUser(ctx)).login;
}
